/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { Toast } from 'sharedUi/Toast';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { InputText } from 'primereact/inputtext';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';

import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import FormSelect from '../../components/forms/form-select';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';

import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import { designationMasterService } from '../../services/designation-master.service';
import { projectsService } from '../../services/projects.service';

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const extractApiError = (error: any): string | null => {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    return detail.message || detail.error?.errorCode || null;
  }
  const msg =
    error?.response?.data?.message ??
    error?.response?.data?.error ??
    error?.message;
  return msg ? String(msg) : null;
};

// -----------------------------------------------------------------------------
// Zod Schema
// -----------------------------------------------------------------------------

const designationMasterSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'Name must be at least 2 characters')
    .max(200, 'Must be <= 200 characters')
    .regex(
      /^[a-zA-Z\-_().\s]+$/,
      'Name can only contain alphabets, hyphen, underscore, parentheses, and dot',
    )
    .refine(
      (v) => (v.match(/[a-zA-Z]/g) || []).length >= 2,
      'Name must contain at least 2 alphabet characters',
    ),
  designationCd: z
    .string()
    .trim()
    .min(1, 'Designation Code is required')
    .max(200, 'Must be <= 200 characters')
    .regex(/^[a-zA-Z0-9_-]+$/, 'Designation Code may contain only letters, digits, hyphens and underscores'),
  appId: z.string().trim().optional().or(z.literal('')),
});

export type DesignationMasterFormData = z.infer<typeof designationMasterSchema>;

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const DesignationMasterForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({
    entity: 'designation-master',
    basePath: '/designation-master',
  });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------

  const { data: existing, isLoading: fetchLoading } = useQuery({
    queryKey: ['designation-master', id],
    queryFn: () => designationMasterService.getById(id!),
    enabled: isEditMode && isReady && !!id,
  });

  // Set breadcrumb title to show designation name instead of UUID in edit mode
  useBreadcrumbTitle(isEditMode ? existing?.name : null);

  // ---------------------------------------------------------------------------
  // Form Setup
  // ---------------------------------------------------------------------------

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<DesignationMasterFormData>({
    resolver: zodResolver(designationMasterSchema) as Resolver<DesignationMasterFormData>,
    defaultValues: {
      name: '',
      designationCd: '',
      appId: '',
    },
    mode: 'onChange',
  });

  // Projects the caller may create in (platform admin = all, else own).
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const projectOptions = accessibleProjects.map((p) => ({
    value: p._id,
    label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
  }));
  const projectLocked = accessibleProjects.length <= 1;
  const selectedAppId = watch('appId');
  useEffect(() => {
    if (!isEditMode && !selectedAppId && accessibleProjects.length) {
      setValue('appId', accessibleProjects[0]._id);
    }
  }, [isEditMode, selectedAppId, accessibleProjects, setValue]);

  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({
    when: isDirty,
  });

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  // Populate form in edit mode
  useEffect(() => {
    if (existing && isEditMode) {
      reset({
        name: existing.name || '',
        designationCd: existing.designationCd || '',
        appId: (existing as any).appId || '',
      });
    }
  }, [existing, isEditMode, reset]);

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const createMut = useMutation({
    mutationFn: (payload: any) => designationMasterService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['designation-master'] });
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Designation created successfully',
        life: 3000,
      });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (error: any) => {
      const apiMsg = extractApiError(error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: apiMsg ? `Save failed: ${apiMsg}` : 'Failed to create designation',
        life: 10000,
      });
    },
  });

  const updateMut = useMutation({
    mutationFn: (payload: any) => designationMasterService.update(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['designation-master'] });
      queryClient.invalidateQueries({ queryKey: ['designation-master', id] });
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Designation updated successfully',
        life: 3000,
      });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (error: any) => {
      const apiMsg = extractApiError(error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: apiMsg ? `Save failed: ${apiMsg}` : 'Failed to update designation',
        life: 10000,
      });
    },
  });

  // Combined loading state for button disable
  const isSaving = createMut.isPending || updateMut.isPending;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const onSubmit = (data: DesignationMasterFormData) => {
    const payload: { name: string; designationCd: string; appId?: string } = {
      name: data.name,
      designationCd: data.designationCd,
    };

    // Owning project (honoured by the BE only for callers who can access it).
    if (data.appId) payload.appId = data.appId;

    if (isEditMode) {
      updateMut.mutate(payload);
    } else {
      createMut.mutate(payload);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (fetchLoading) {
    return (
      <PageSkeleton />
    );
  }

  return (
    <PermissionGuard jobName={JOB_NAMES.DESIGNATION_MASTER}>
      <Toast ref={toast} position="top-right" />
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900" data-testid={isEditMode ? 'SCR-DesignationMaster-Edit' : 'SCR-DesignationMaster-Create'}>
        {/* Header - Compact */}
        <ScreenHeader
          icon="pi pi-id-card"
          title={isEditMode ? 'Edit Designation' : 'Create New Designation'}
          subtitle={isEditMode ? 'Update designation configuration' : 'Add a new designation for the organization'}
          onBack={() => handleNavigate('/designation-master')}
        />

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="ds-card">
            {/* Section: Designation Information */}
            <div className="p-4 border-b border-line">
              <h3 className="text-sm font-medium text-ink-muted uppercase tracking-wide mb-3 flex items-center gap-2">
                <i className="pi pi-id-card" />
                Designation Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Name */}
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <InputText
                        id="name"
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        placeholder="Enter designation name (e.g., Manager, Developer)"
                        className={`w-full ${errors.name ? 'p-invalid' : ''}`}
                      />
                      {errors.name && (
                        <small className="text-red-500">{errors.name.message}</small>
                      )}
                    </div>
                  )}
                />

                {/* Designation Code */}
                <Controller
                  name="designationCd"
                  control={control}
                  render={({ field }) => (
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Designation Code <span className="text-red-500">*</span>
                      </label>
                      <InputText
                        id="designationCd"
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        placeholder="Enter designation code (e.g., MGR, DEV)"
                        className={`w-full ${errors.designationCd ? 'p-invalid' : ''}`}
                      />
                      {errors.designationCd && (
                        <small className="text-red-500">{errors.designationCd.message}</small>
                      )}
                    </div>
                  )}
                />

                {/* Owning project */}
                <Controller
                  name="appId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormSelect
                      {...field}
                      value={value || ''}
                      onChange={(e) => onChange(e.target.value)}
                      label="Project"
                      options={projectOptions}
                      placeholder="Select project"
                      disabled={projectLocked || isEditMode}
                      error={!!errors.appId}
                      helperText={errors.appId?.message}
                    />
                  )}
                />
              </div>
            </div>

            {/* Section: Form Actions */}
            <div className="p-4 border-t border-line">
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  label="Cancel"
                  severity="secondary"
                  outlined
                  onClick={() => handleNavigate('/designation-master')}
                  disabled={isSaving}
                />
                <Button
                  type="submit"
                  label={isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
                  icon={isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                  disabled={isSaving}
                />
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Leave confirmation */}
      <DiscardChangesDialog
        visible={showLeaveDialog}
        onStay={cancelLeave}
        onLeave={confirmLeave}
        testId="DesignationMasterForm.Dialog.Leave"
      />
    </PermissionGuard>
  );
};

export default DesignationMasterForm;
